// Footer subscribe form initializer.
// Reads portal/form/region IDs from the [data-hubspot-form] container's
// data-* attributes — which in turn are populated from theme settings
// (see config/settings_schema.json → "HubSpot integration"), avoiding
// hardcoded duplication across multiple files.
//
// Also patches HubSpot's rendered markup for a11y: HubSpot's embed does not
// output `autocomplete`, `aria-required`, or `role="alert"`, and renders
// broken ARIA on radio groups (role="checkbox" on <ul>, missing aria-checked
// on <li role="radio">). We patch once when the form is ready and again on
// every re-render (HubSpot re-renders error markup on validation failure).

(function initFooterHubspotForm() {
  const formEl = /** @type {HTMLElement | null} */ (document.querySelector('[data-hubspot-form]'));
  if (!formEl) return;

  const portalId = formEl.dataset.portalId;
  const formId = formEl.dataset.formId;
  const region = formEl.dataset.region || 'na1';
  if (!portalId || !formId) return;

  const AUTOCOMPLETE_MAP = {
    firstname: 'given-name',
    lastname: 'family-name',
    fullname: 'name',
    name: 'name',
    email: 'email',
    phone: 'tel',
    mobilephone: 'tel',
    company: 'organization',
    jobtitle: 'organization-title',
    address: 'street-address',
    city: 'address-level2',
    state: 'address-level1',
    zip: 'postal-code',
    country: 'country-name',
  };

  // Strip HubSpot's broken ARIA on radio groups. HubSpot renders:
  //   <ul role="checkbox">                 ← wrong role
  //     <li role="radio">                  ← nested with native <input type="radio">
  //       <input type="radio">
  //
  // This violates WCAG 4.1.2 (nested interactive controls + role mismatch).
  // Native <input type="radio"> is fully accessible — no ARIA needed.
  // We strip the bad roles so the native HTML semantics work cleanly.
  const patchRadioGroups = (root) => {
    root.querySelectorAll('ul.inputs-list[role="checkbox"]').forEach((ul) => {
      ul.removeAttribute('role');
    });

    root.querySelectorAll('li.hs-form-radio[role="radio"]').forEach((li) => {
      li.removeAttribute('role');
      li.removeAttribute('aria-checked');
    });
  };

  const patchForm = (root) => {
    if (!root) return;

    root.querySelectorAll('input, textarea, select').forEach((field) => {
      const name = (field.name || '').toLowerCase().replace(/.*_/, '');
      const ac = AUTOCOMPLETE_MAP[name];
      if (ac && !field.hasAttribute('autocomplete')) {
        field.setAttribute('autocomplete', ac);
      }
      if (field.required && !field.hasAttribute('aria-required')) {
        field.setAttribute('aria-required', 'true');
      }
    });

    root.querySelectorAll('.hs-error-msgs, .hs-error-msg').forEach((errEl) => {
      if (!errEl.hasAttribute('role')) errEl.setAttribute('role', 'alert');
      if (!errEl.hasAttribute('aria-live')) errEl.setAttribute('aria-live', 'polite');
    });

    patchRadioGroups(root);
  };

  // HubSpot may render the form at document.body root (not inside our
  // [data-hubspot-form] container), depending on embed version + config.
  // So we watch the entire document for any .hbspt-form that appears,
  // and patch each form (plus its dynamic re-renders) wherever it lands.
  const patchAllHubSpotForms = () => {
    document.querySelectorAll('.hbspt-form form, form.hs-form, form[id^="hsForm_"]').forEach(
      /** @param {Element} el */
      (el) => {
        const f = /** @type {HTMLFormElement} */ (el);
        if (f.dataset.a11yPatched) return;
        f.dataset.a11yPatched = '1';
        patchForm(f);
        // Re-patch on validation re-render
        const innerObserver = new MutationObserver(() => patchForm(f));
        innerObserver.observe(f, { childList: true, subtree: true });
      }
    );
  };

  // Initial scan + watch document.body for HubSpot's async form injection.
  const observeDocument = () => {
    patchAllHubSpotForms();
    const docObserver = new MutationObserver(patchAllHubSpotForms);
    docObserver.observe(document.body, { childList: true, subtree: true });
  };

  const create = () => {
    /** @type {any} */
    const w = window;
    if (!w.hbspt || typeof w.hbspt.forms?.create !== 'function') return false;
    w.hbspt.forms.create({
      portalId,
      formId,
      region,
      onFormReady: () => patchAllHubSpotForms(),
    });
    observeDocument();
    return true;
  };

  if (create()) return;

  // HubSpot embed loads asynchronously — poll briefly until ready, then init.
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    if (create() || attempts > 50) clearInterval(timer);
  }, 100);
})();
